<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

/**
 * Hero: großes typografisches Statement mit 3D-Split-Flap-Flip (kein Reflow:
 * unsichtbare Sizer reservieren die Breite, Faces absolut gestapelt, nur
 * rotateX). Rechts das Konzept „Chaos schwebt um Ordnung": verstreute
 * Discord-Channel-/DM-Chips treiben im Hinter- UND Vordergrund (Tiefe über
 * scale + Deckkraft, bewusst ohne blur-Filter — der kostet bei dauerhaft
 * bewegten Elementen pro Frame) um ein scharfes, aufgeräumtes Forum-Panel in
 * der Mitte — Clarifys Kernversprechen als Bild. Einmalige Entrance,
 * langsame Idle-Floats, die off-screen pausieren; bei reduced-motion statisch.
 */

const { scrollToTarget } = useSmoothScroll()

/* ── Hero-Backdrop wählen (Ein-Wort-Schalter) ──────────────────────────────
   HERO_BACKDROP: 'plasma' → PlasmaWave (Vue Bits, WebGL-Welle)
                  'dots'   → DotField   (Vue Bits, interaktives Punktfeld)
                  'beam'   → statischer Lichtstreifen (kein Canvas)
   Nur den Wert ändern — mehr braucht ein Wechsel oder Rückbau nicht.
   Die Canvas-Variante läuft nur ab 768px und ohne reduced-motion,
   sonst greift automatisch der Beam. */
const HERO_BACKDROP = 'plasma' as 'dots' | 'plasma' | 'beam'
const backdrop = ref<'dots' | 'plasma' | 'beam'>('beam')

const words = ['CHANNEL CHAOS', 'MISSING CONTEXT', 'LOST TICKETS', 'COPY-PASTE']

/* Die chaotische Außenwelt in drei Tiefen-Ebenen (Tunnel-Blick: fern =
   klein & blass nahe der Mitte, nah = groß & weich am Rand). Durchweg
   Channels mit # — so sieht Support ohne Clarify aus: ein Channel pro
   Ticket, dazu die üblichen Sammel-Channels. */
const chaosFar = [
  { label: 'general', count: '99+' },
  { label: 'help', count: '23' },
  { label: 'ticket-mira', count: '12' },
  { label: 'faq', count: '7' },
  { label: 'ticket-cowcat' },
  { label: 'ticket-lea' }
]
const chaosMid = [
  { label: 'support-2', count: '47' },
  { label: 'ticket-jonas', count: '3' },
  { label: 'tickets-old', count: '128' },
  { label: 'where-do-i-ask', count: '56' },
  { label: 'ticket-max' },
  { label: 'ticket-nia' }
]
const chaosNear = [
  { label: 'ticket-tom', count: '7' },
  { label: 'urgent!!', count: '31' },
  { label: 'questions', count: '64' }
]

const filterTags = [
  { label: 'Billing', emoji: '💳' },
  { label: 'Tech', emoji: '🔧' },
  { label: 'General', emoji: '💬' }
]

/* Die geordnete Mitte: das Forum, das Clarify daraus macht.
   Interaktives Gimmick — Freitextsuche + Kategorie-Filter über Mockdaten. */
const allPosts = [
  { tag: { label: 'Billing', emoji: '💳' }, title: 'jonas.-Billing | Premium role missing', count: 4, time: 'just now' },
  { tag: { label: 'Tech', emoji: '🔧' }, title: 'mira-Tech | Bot won’t respond to /verify', count: 7, time: '25m ago' },
  { tag: { label: 'General', emoji: '💬' }, title: 'tom_k-General | Server boost rewards', count: 2, time: '1h ago' },
  { tag: { label: 'Billing', emoji: '💳' }, title: 'lea-Billing | Double charge on renewal', count: 3, time: '2h ago' },
  { tag: { label: 'Tech', emoji: '🔧' }, title: 'max-Tech | Webhook not firing', count: 5, time: '4h ago' },
  { tag: { label: 'General', emoji: '💬' }, title: 'nia-General | How do I change my name?', count: 1, time: '6h ago' }
]

/* Fakten-Band unter dem Hero: echte Kennzahlen des Produkts (kein erfundenes
   Marketing-Zählwerk) — sie zählen bei der Entrance trotzdem hoch. */
const stats = [
  { value: 3, title: 'Delivery modes', text: 'DMs, private threads or channels' },
  { value: 5, title: 'Modules', text: 'Turned on only when you need them' },
  { value: 6, title: 'Integrations', text: 'From Stripe to signed webhooks' }
]
const statValue = (v: number) => String(Math.round(v))

const query = ref('')
const activeTag = ref<string | null>(null)

const filteredPosts = computed(() =>
  allPosts.filter(p =>
    (!activeTag.value || p.tag.label === activeTag.value)
    && (!query.value.trim() || p.title.toLowerCase().includes(query.value.trim().toLowerCase()))
  )
)
const visiblePosts = computed(() => filteredPosts.value.slice(0, 3))
const hiddenCount = computed(() => Math.max(0, filteredPosts.value.length - 3))

function toggleTag(label: string) {
  activeTag.value = activeTag.value === label ? null : label
}

function resetFilters() {
  activeTag.value = null
  query.value = ''
}

const heroRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | undefined

/* ── Logo-Dock ──────────────────────────────────────────────────────────────
   Das große weiße Logo über dem Titel fliegt nach einem Schwellwert in den
   Logo-Slot der Navbar (der Schriftzug „Clarify" steht dort fest) und beim
   Hochscrollen zurück. Den Flug übernimmt ein fixed „Flyer"; Start- und
   Zielslot sind währenddessen unsichtbar (Klassen auf <html>, main.css).

   Choreografie mit dem Nav-Morph (useNavDock), damit sich beides nicht in
   die Quere kommt:
     runter: Logo fliegt in die noch große Nav → kurze Pause → Nav wird Pill
     hoch:   Nav wird wieder Balken → kurze Pause → Logo fliegt zurück
   Ein Richtungswechsel mitten drin setzt dort an, wo das Logo gerade ist. */
const DOCK_THRESHOLD = 110 // px: Logo-Oberkante ↔ Viewport-Oberkante
const DOCK_FLIGHT = 0.7 // s
const DOCK_PAUSE = 0.2 // s Luft zwischen Logo-Landung und Nav-Morph
let undock: (() => void) | undefined

function setupLogoDock(heroMark: HTMLElement, animate: boolean) {
  const navMark = document.querySelector<HTMLElement>('.site-nav-logo')
  if (!navMark) return
  const root = document.documentElement
  const navDock = useNavDock()
  const releaseNav = navDock.claim()

  const flyer = document.createElement('img')
  flyer.src = heroMark.getAttribute('src') ?? ''
  flyer.alt = ''
  flyer.className = 'logo-flyer'
  flyer.setAttribute('aria-hidden', 'true')
  document.body.appendChild(flyer)

  let want = false // Zielzustand laut Scroll-Position
  let logoAt: 'hero' | 'nav' | 'flying' = 'hero'
  let flight: gsap.core.Tween | undefined
  let seq: gsap.core.Timeline | undefined

  /* Größe aus der Höhe: der Nav-Slot fährt in der Breite auf (0 → 32px) */
  const stateOf = (el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    return { x: r.left, y: r.top, s: r.height / heroMark.offsetHeight }
  }
  const flyerState = () => ({
    x: gsap.getProperty(flyer, 'x') as number,
    y: gsap.getProperty(flyer, 'y') as number,
    s: gsap.getProperty(flyer, 'scale') as number
  })

  /* Ein Flug zum Ziel-Slot — startet dort, wo das Logo gerade ist. Das Ziel
     wird pro Frame live gemessen (der Slot fährt auf, die Seite scrollt). */
  const flyTo = (dest: 'hero' | 'nav') => {
    const from = logoAt === 'flying' ? flyerState() : stateOf(logoAt === 'nav' ? navMark : heroMark)
    flight?.kill()
    logoAt = 'flying'
    root.classList.toggle('logo-docked', dest === 'nav')
    root.classList.add('logo-flying')
    flyer.style.width = `${heroMark.offsetWidth}px`
    flyer.style.height = `${heroMark.offsetHeight}px`
    gsap.set(flyer, { x: from.x, y: from.y, scale: from.s })

    const target = dest === 'nav' ? navMark : heroMark
    const p = { t: 0 }
    flight = gsap.to(p, {
      t: 1,
      duration: DOCK_FLIGHT,
      ease: 'power3.inOut',
      onUpdate: () => {
        const to = stateOf(target)
        gsap.set(flyer, {
          x: from.x + (to.x - from.x) * p.t,
          y: from.y + (to.y - from.y) * p.t,
          scale: from.s + (to.s - from.s) * p.t
        })
      },
      onComplete: () => {
        flight = undefined
        logoAt = dest
        root.classList.remove('logo-flying')
      }
    })
    return flight
  }

  const go = (toDock: boolean) => {
    if (toDock === want) return
    want = toDock
    seq?.kill()
    const nav = navDock.nav()

    if (!animate) {
      flight?.kill()
      logoAt = toDock ? 'nav' : 'hero'
      root.classList.toggle('logo-docked', toDock)
      root.classList.remove('logo-flying')
      nav?.setCompact(toDock, false)
      return
    }

    seq = gsap.timeline()
    if (toDock) {
      // Logo rein (falls nicht schon drin) … Pause … dann erst die Pill
      if (logoAt !== 'nav') seq.add(flyTo('nav'))
      seq.call(() => nav?.setCompact(true), [], `+=${DOCK_PAUSE}`)
    } else {
      // Palindrom: erst der Balken … Pause … dann fliegt das Logo zurück
      if (nav?.isCompact()) {
        seq.call(() => nav.setCompact(false))
        seq.to({}, { duration: NAV_MORPH_DURATION + DOCK_PAUSE })
      }
      if (logoAt !== 'hero') seq.call(() => { flyTo('hero') })
    }
  }

  // Startzustand ohne Flug (z. B. bei wiederhergestellter Scroll-Position)
  want = heroMark.getBoundingClientRect().top < DOCK_THRESHOLD
  logoAt = want ? 'nav' : 'hero'
  root.classList.toggle('logo-docked', want)
  navDock.nav()?.setCompact(want, false)

  // Nur die Start-Grenze zählt (runter = andocken, hoch = zurück) — ein
  // End-Wert ('max') wird mit Element-Trigger auf Mobile zu früh erreicht
  ScrollTrigger.create({
    trigger: heroMark,
    start: `top ${DOCK_THRESHOLD}px`,
    onEnter: () => go(true),
    onLeaveBack: () => go(false)
  })

  undock = () => {
    seq?.kill()
    flight?.kill()
    flyer.remove()
    root.classList.remove('logo-docked', 'logo-flying')
    releaseNav()
  }
}

onMounted(async () => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  // Canvas-Backdrop nur bei Motion-Erlaubnis und ab Tablet-Breite (GPU-Budget)
  backdrop.value
    = !reduced && window.matchMedia('(min-width: 768px)').matches ? HERO_BACKDROP : 'beam'
  // Auf den Re-Render warten, damit der Backdrop-Layer für den Kontext existiert
  await nextTick()
  gsap.registerPlugin(ScrollTrigger)

  ctx = gsap.context((self) => {
    const q = self.selector!
    const heroMark = q('.hero-mark')[0] as HTMLElement | undefined
    // Bei reduced-motion wechselt das Logo ohne Flug den Platz
    if (heroMark) setupLogoDock(heroMark, !reduced)

    const faces = gsap.utils.toArray<HTMLElement>(q('.hero-flip-face'))
    const bracketR = q('.hero-flip-bracket-r')[0] as HTMLElement | undefined
    const flipBox = q('.hero-flip')[0] as HTMLElement | undefined

    /* Rechte Klammer folgt der gemessenen Wortbreite — transform-only,
       die reservierte Maximalbreite bleibt (kein Reflow im Fließtext). */
    const bracketX = (i: number) => {
      const word = faces[i]?.querySelector('.hero-flip-word') as HTMLElement | null
      if (!word || !flipBox) return 0
      return Math.min(0, word.offsetWidth - flipBox.offsetWidth)
    }
    // Erst nach dem Font-Load messen — vorher stimmen die Breiten nicht.
    document.fonts?.ready.then(() => {
      if (bracketR) gsap.set(bracketR, { x: bracketX(0) })
    }).catch(() => undefined)

    if (reduced) {
      gsap.set(faces[0]!, { rotateX: 0, autoAlpha: 1 })
      return
    }

    /* ── Split-Flap-Flip: bewusst getimter Zyklus, weiches Settle ─────────── */
    gsap.set(faces, { rotateX: -92, autoAlpha: 0, transformOrigin: '50% 0%' })
    gsap.set(faces[0]!, { rotateX: 0, autoAlpha: 1 })

    const DWELL = 2.7
    // repeatRefresh: function-based Werte (Klammer-Ziele) werden bei jedem
    // Zyklus neu evaluiert — Resize heilt sich damit von selbst.
    const flipTl = gsap.timeline({ repeat: -1, delay: DWELL, repeatRefresh: true })
    faces.forEach((face, i) => {
      const next = faces[(i + 1) % faces.length]!
      const at = i * DWELL
      // Ausgehendes Face kippt nach unten weg (Hinge unten) …
      flipTl.to(face, {
        rotateX: 92,
        autoAlpha: 0,
        transformOrigin: '50% 100%',
        duration: 0.42,
        ease: 'power3.in'
      }, at)
      // … das neue fällt von oben ein und settlet mit leichtem Überschwung.
      // immediateRender aus: sonst versteckt der letzte Zyklus-Schritt
      // (next = faces[0]) das erste Wort sofort beim Aufbau → leere Klammer.
      flipTl.fromTo(next,
        { rotateX: -92, autoAlpha: 0, transformOrigin: '50% 0%' },
        { rotateX: 0, autoAlpha: 1, duration: 0.62, ease: 'back.out(1.4)', immediateRender: false },
        at + 0.3)
      // Die Klammer fährt synchron auf die Breite des neuen Wortes.
      if (bracketR) {
        flipTl.to(bracketR, {
          x: () => bracketX((i + 1) % faces.length),
          duration: 0.62,
          ease: 'power3.inOut'
        }, at + 0.18)
      }
    })
    // Auf volle Zykluslänge auffüllen, sonst wird das letzte Wort beim
    // Repeat-Wrap um seine Verweildauer gebracht (delay gilt nur vor Runde 1).
    const pad = faces.length * DWELL - flipTl.duration()
    if (pad > 0) flipTl.to({}, { duration: pad })

    /* ── Entrance: eine Timeline, ein Rhythmus ────────────────────────────── */
    // fromTo + opacity (nicht autoAlpha): der Pre-Paint-Startzustand kommt aus
    // CSS (`.js .hero-rise …`, main.css) — kein Aufblitzen des SSR-Endzustands
    // nach der Hydration, und der H1/LCP-Text bleibt im Accessibility-Tree.
    // Die Chaos-Chips behalten ihre CSS-Deckkraft → weiter via from().
    const enter = gsap.timeline({ defaults: { ease: 'power3.out' } })
    enter
      .fromTo(q('.hero-rise'), { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.85, stagger: 0.08 }, 0.05)
      .fromTo(q('.hero-order'), { opacity: 0, y: 26, scale: 0.98 }, { opacity: 1, y: 0, scale: 1, duration: 0.9 }, 0.3)
      .from(q('.hero-chaos'), { autoAlpha: 0, scale: 0.85, duration: 0.7, ease: 'power2.out', stagger: 0.05 }, 0.4)
      .fromTo(q('.hero-proof'), { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.8 }, 0.55)

    /* Count-up im Proof-Band — Feedback, dass hier echte Zahlen stehen */
    gsap.utils.toArray<HTMLElement>(q('.hero-stat-value')).forEach((el, i) => {
      const stat = stats[i]
      if (!stat) return
      const proxy = { v: 0 }
      el.textContent = statValue(0) // Startwert sofort — kein Rücksprung mitten im Fade
      gsap.to(proxy, {
        v: stat.value,
        duration: 1.5,
        ease: 'power3.out',
        delay: 0.75 + i * 0.12,
        onUpdate: () => {
          el.textContent = statValue(proxy.v)
        }
      })
    })
    /* Alle Endlos-Loops (Flip, Chip-Drift, Atmen) hängen in EINER Container-
       Timeline, die nur läuft, solange der Hero im Viewport ist — sonst
       tickt GSAP für ~17 unsichtbare Tweens auf jeder Scroll-Frame weiter. */
    const loops = gsap.timeline()
    loops.add(flipTl, 0)

    // Parallax-Drift pro Tiefen-Ebene: fern treibt kaum, nah treibt deutlich
    const drift = (sel: string, amp: number, dur: number) => {
      gsap.utils.toArray<HTMLElement>(q(sel)).forEach((chip, i) => {
        loops.add(gsap.to(chip, {
          y: (i % 2 ? -1 : 1) * (amp + (i % 3) * amp * 0.4),
          x: (i % 2 ? 1 : -1) * amp * 0.6,
          rotation: (i % 2 ? 1 : -1) * (amp > 10 ? 2.5 : 1.2),
          duration: dur + (i % 4) * 0.8,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          delay: i * 0.3
        }), 0)
      })
    }
    drift('.hero-chaos-far', 5, 5.5)
    drift('.hero-chaos-mid', 10, 4.5)
    drift('.hero-chaos-near', 16, 3.8)
    // Die geordnete Mitte atmet nur — sie steht ruhig im Fokus
    loops.add(gsap.to(q('.hero-order'), { y: -2.5, duration: 6.5, ease: 'sine.inOut', repeat: -1, yoyo: true, delay: 1.4 }), 0)

    ScrollTrigger.create({
      trigger: heroRef.value,
      start: 'top bottom',
      end: 'bottom top',
      onToggle: self => (self.isActive ? loops.play() : loops.pause())
    })

    // Beam/Plasma parallaxen beim Verlassen des Heros langsamer als der
    // Content — Tiefe über eine einzige scrub-gebundene Transform.
    // (Bei HERO_BACKDROP 'dots' gibt es keinen solchen Layer → kein Trigger.)
    const parallaxLayers = q('.hero-beam, .hero-plasma')
    if (parallaxLayers.length) {
      gsap.to(parallaxLayers, {
        yPercent: 24,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.value,
          start: 'top top',
          end: 'bottom top',
          scrub: 1
        }
      })
    }
  }, heroRef.value!)
})

onBeforeUnmount(() => {
  undock?.()
  ctx?.revert()
})
</script>

<template>
  <section ref="heroRef" class="hero">
    <!-- Ambient-Gradient über den gesamten Hero — per Maske in sich ausgefedert -->
    <div class="hero-ambient" aria-hidden="true" />
    <!-- ── Hero-Backdrop, umschaltbar via HERO_BACKDROP (s. Script) ──────── -->
    <div v-if="backdrop === 'dots'" class="hero-dots" aria-hidden="true">
      <ClientOnly>
        <!-- Spacing 18 statt 15 (~30 % weniger Punkte) und keine Dauerwelle:
             ohne Cursor-Einfluss steht das Bild und der Canvas-Loop ruht -->
        <LazyDotField
          :dot-radius="1.6"
          :dot-spacing="18"
          :bulge-strength="58"
          :glow-radius="180"
          :cursor-radius="420"
          :cursor-force="0.1"
          :wave-amplitude="0"
          :sparkle="false"
          bulge-only
          gradient-from="rgba(135, 131, 255, 0.38)"
          gradient-to="rgba(93, 36, 255, 0.3)"
          glow-color="#0b0b10"
        />
      </ClientOnly>
    </div>
    <!-- Lazy-Komponenten: jede Backdrop-Variante ist ein eigener Chunk, die
         WebGL-Bibliothek (ogl) lädt nur, wenn 'plasma' wirklich rendert -->
    <div v-else-if="backdrop === 'plasma'" class="hero-plasma" aria-hidden="true">
      <ClientOnly>
        <LazyPlasmaWave
          :colors="['#5d24ff', '#8783ff']"
          :speed1="0.04"
          :speed2="0.04"
          :dir2="-1"
          :bend1="1"
          :bend2="0.6"
          :focal-length="0.8"
        />
      </ClientOnly>
    </div>
    <!-- Fallback: statischer Lichtstreifen (Mobile, reduced-motion, 'beam') -->
    <div v-else class="hero-beam" aria-hidden="true" />
    <div class="section-shell hero-grid">
      <div class="hero-copy shell-gutter">
        <!-- Großes Logo: fliegt beim Runterscrollen in den Logo-Slot der Nav
             (Logo-Dock, s. Script) — der Schriftzug steht dort fest -->
        <div class="hero-mark-slot hero-rise">
          <img src="/brand/clarify-mark-white.svg" alt="" width="88" height="88" class="hero-mark">
        </div>
        <span class="eyebrow hero-rise">The ticket bot for Discord</span>

        <h1 class="hero-title hero-rise">
          NO MORE<br>
          <span class="hero-flip-frame">
            <span class="hero-flip-bracket hero-flip-bracket-l" aria-hidden="true">[</span>
            <span class="hero-flip" aria-hidden="true">
              <!-- Breiten-Reserve: Text kommt aus CSS content (attr), nicht aus
                   dem DOM — sonst stünde jedes Wort doppelt im H1 (Crawler) -->
              <span v-for="w in words" :key="`sizer-${w}`" class="hero-flip-sizer" :data-word="w" />
              <span v-for="(w, i) in words" :key="w" class="hero-flip-face" :class="{ 'is-initial': i === 0 }"><span class="hero-flip-word">{{ w }}</span></span>
            </span>
            <span class="hero-flip-bracket hero-flip-bracket-r" aria-hidden="true">]</span>
          </span>
          <!-- Stabiler, vollständiger Heading-Text für AT + Suchmaschinen
               (der Flip wechselt visuell alle 2.7 s) -->
          <span class="sr-only">Channel chaos, missing context, lost tickets, copy-paste. Clarify, the ticket bot for Discord.</span>
        </h1>

        <p class="hero-sub copy-muted hero-rise">
          Members open a ticket from your panel or a DM and stay in their inbox.
          Your team gets a tagged forum thread with their answers and payment details
          already attached. Clarify carries every reply between the two.
        </p>

        <div class="hero-ctas hero-rise">
          <UButton
            label="Start for free"
            size="xl"
            trailing-icon="i-lucide-arrow-right"
            class="hero-cta-primary"
            to="https://app.clarify.bot"
            target="_blank"
          />
          <UButton
            label="See how it works"
            size="xl"
            color="neutral"
            variant="ghost"
            trailing-icon="i-lucide-arrow-down"
            class="hero-cta-secondary"
            @click="scrollToTarget('#funktionsweise')"
          />
        </div>
      </div>

      <!-- Konzept: Chaos-Tunnel (drei Tiefen-Ebenen) um das geordnete Forum.
           Die Karte in der Mitte ist interaktiv (Suche + Tag-Filter). -->
      <div class="hero-visual">

        <!-- Ebene 1: fern — klein, stark unscharf, nahe der Mitte -->
        <div
          v-for="(c, i) in chaosFar"
          :key="`far-${c.label}`"
          class="hero-chaos hero-chaos-far"
          :class="`hero-chaos-far${i}`"
          aria-hidden="true"
        >
          <span class="hero-chaos-hash" data-text="#" />
          <span class="hero-chaos-label" :data-text="c.label" />
          <span v-if="c.count" class="hero-chaos-badge" :data-text="c.count" />
        </div>

        <!-- Ebene 2: mittel -->
        <div
          v-for="(c, i) in chaosMid"
          :key="`mid-${c.label}`"
          class="hero-chaos hero-chaos-mid"
          :class="`hero-chaos-mid${i}`"
          aria-hidden="true"
        >
          <span class="hero-chaos-hash" data-text="#" />
          <span class="hero-chaos-label" :data-text="c.label" />
          <span v-if="c.count" class="hero-chaos-badge" :data-text="c.count" />
        </div>

        <!-- Die Mitte: ein erkennbares, aufgeräumtes Forum -->
        <div class="hero-order">
          <div class="hero-order-head">
            <img src="/brand/clarify-icon-square.svg" alt="" width="19" height="19" class="hero-order-logo">
            <span class="hero-order-name">support-tickets</span>
          </div>

          <!-- Forum-Signatur: echte Suche + New Post -->
          <div class="hero-order-toolbar">
            <label class="hero-order-search">
              <UIcon name="i-lucide-search" class="size-[13px]" />
              <input
                v-model="query"
                type="text"
                class="hero-order-search-input"
                placeholder="Search or create a post…"
                aria-label="Search posts"
              >
            </label>
            <span class="hero-order-newpost" aria-hidden="true">
              <UIcon name="i-lucide-message-circle" class="size-[12px]" />
              New Post
            </span>
          </div>

          <!-- Kategorien: klickbar — filtert die Liste -->
          <div class="hero-order-filters">
            <span class="hero-order-sort" aria-hidden="true">
              <UIcon name="i-lucide-arrow-up-down" class="size-[11px]" />
              Sort &amp; view
            </span>
            <span class="hero-order-filters-divider" aria-hidden="true" />
            <button
              v-for="t in filterTags"
              :key="t.label"
              type="button"
              class="hero-order-tagbtn"
              :aria-pressed="activeTag === t.label"
              @click="toggleTag(t.label)"
            >
              <DiscordTag :label="t.label" :emoji="t.emoji" :active="activeTag === t.label" />
            </button>
            <button type="button" class="hero-order-all" @click="resetFilters">
              All
              <UIcon name="i-lucide-chevron-down" class="size-[11px]" />
            </button>
          </div>

          <!-- Gefilterte Post-Zeilen. Kategorie-Wechsel = ein sauberer
               out-in-Crossfade der ganzen Liste (kein Item-Geflacker);
               Tippen filtert bewusst instant. Höhe ist fixiert. -->
          <Transition name="swap" mode="out-in">
            <div :key="activeTag ?? 'all'" class="hero-order-list">
              <div v-for="p in visiblePosts" :key="p.title" class="hero-order-row">
                <div class="hero-order-main">
                  <DiscordTag :label="p.tag.label" :emoji="p.tag.emoji" />
                  <div class="hero-order-title">{{ p.title }}</div>
                  <div class="hero-order-author">Clarify</div>
                  <div class="hero-order-meta">
                    <span class="hero-order-envelope">
                      <UIcon name="i-lucide-mail" class="size-[11px]" />
                    </span>
                    <UIcon name="i-lucide-message-circle" class="size-[12px]" />
                    {{ p.count }}
                    <span class="hero-order-dot" />
                    {{ p.time }}
                  </div>
                </div>
                <img class="hero-order-thumb" src="/brand/clarify-icon-square.svg" alt="" width="36" height="36">
              </div>
              <div v-if="!visiblePosts.length" class="hero-order-empty">
                <UIcon name="i-lucide-search-x" class="size-[15px]" />
                No posts found. Try another filter.
              </div>
            </div>
          </Transition>
          <!-- Fester Slot: Höhe bleibt konstant, ob Zeile da ist oder nicht -->
          <div class="hero-order-footerline">
            <span v-if="hiddenCount && visiblePosts.length">
              +{{ hiddenCount }} more {{ hiddenCount === 1 ? 'post' : 'posts' }}
            </span>
          </div>
        </div>

        <!-- Ebene 3: nah — groß, weich, am Rand (Vordergrund) -->
        <div
          v-for="(c, i) in chaosNear"
          :key="`near-${c.label}`"
          class="hero-chaos hero-chaos-near"
          :class="`hero-chaos-near${i}`"
          aria-hidden="true"
        >
          <span class="hero-chaos-hash" data-text="#" />
          <span class="hero-chaos-label" :data-text="c.label" />
          <span class="hero-chaos-badge" :data-text="c.count" />
        </div>
      </div>
    </div>

    <!-- Fakten-Band: drei Zellen im Gerüst — Hairline-Trenner mit Plus-Marks
         an den Kreuzungen, Akzent-Dash auf der Oberkante jeder Zelle -->
    <div class="hero-proof">
      <span class="section-rule" aria-hidden="true" />
      <div class="section-shell hero-proof-grid">
        <div v-for="(s, i) in stats" :key="s.title" class="hero-stat">
          <template v-if="i">
            <span class="cell-plus cell-plus-tl hero-stat-plus" aria-hidden="true" />
            <span class="cell-plus cell-plus-bl hero-stat-plus" aria-hidden="true" />
          </template>
          <span class="hero-stat-value">{{ statValue(s.value) }}</span>
          <span class="hero-stat-copy">
            <span class="hero-stat-title">{{ s.title }}</span>
            <span class="hero-stat-text">{{ s.text }}</span>
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  padding: clamp(150px, 17vh, 190px) 0 0; /* Zahlen-Band sitzt direkt auf dem nächsten Divider */
  overflow: hidden;
}

/* Ambient-Gradient über den kompletten Hero. Die radiale Maske federt ihn
   in alle Richtungen auf null aus — nirgendwo eine sichtbare Schnittkante. */
.hero-ambient {
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(46% 42% at 70% 34%, var(--nb-accent-tint-strong), transparent 68%),
    radial-gradient(38% 36% at 22% 72%, rgba(140, 198, 246, 0.08), transparent 64%),
    radial-gradient(30% 30% at 48% 12%, var(--nb-accent-tint), transparent 66%);
  mask-image: radial-gradient(72% 62% at 50% 48%, #000 34%, transparent 82%);
  -webkit-mask-image: radial-gradient(72% 62% at 50% 48%, #000 34%, transparent 82%);
  pointer-events: none;
}

/* DotField-Layer: interaktives Punktfeld über den ganzen Hero, zu den
   Rändern weich ausmaskiert. Cursor-Bulge kommt aus der Komponente selbst,
   deshalb bewusst OHNE Parallax (Cursor-Mapping bliebe sonst nicht exakt). */
.hero-dots {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0;
  mask-image: radial-gradient(118% 100% at 50% 26%, #000 36%, transparent 82%);
  -webkit-mask-image: radial-gradient(118% 100% at 50% 26%, #000 36%, transparent 82%);
  animation: hero-backdrop-in 1.4s var(--ease-out) 0.15s forwards;
}

@keyframes hero-backdrop-in {
  to {
    opacity: 1;
  }
}

/* PlasmaWave-Layer: volle Hero-Breite, weich ausmaskiert, unter dem Content.
   Die Welle rendert transparent — die Intensität steuert die Opacity. Kein
   filter: der Canvas ändert sich jede Frame, ein Filter käme on top. */
.hero-plasma {
  position: absolute;
  inset: -6% 0 0;
  z-index: 0;
  opacity: 0;
  pointer-events: none;
  /* Energie oben rechts hinter dem Visual — die Headline-Zone bleibt ruhig */
  mask-image: radial-gradient(105% 78% at 64% 14%, #000 22%, transparent 72%);
  -webkit-mask-image: radial-gradient(105% 78% at 64% 14%, #000 22%, transparent 72%);
  will-change: transform;
  animation: hero-plasma-in 1.6s var(--ease-out) 0.2s forwards;
}

@keyframes hero-plasma-in {
  to {
    opacity: 0.26;
  }
}

/* Angewinkelter Lichtstreifen über die volle Breite — das eine große
   Hintergrund-Element des Heros, per Scrub-Parallax entkoppelt. */
.hero-beam {
  position: absolute;
  top: -14%;
  left: 50%;
  width: 132vw;
  height: 60%;
  transform: translateX(-50%) rotate(-7deg);
  /* Weiche Kanten über lange Verlaufsstopps statt filter: blur — der Beam
     parallaxt per Transform, ein Blur-Filter würde jede Frame neu rechnen */
  background: linear-gradient(
    100deg,
    transparent 24%,
    rgba(135, 131, 255, 0.05) 36%,
    rgba(135, 131, 255, 0.12) 46%,
    rgba(140, 198, 246, 0.08) 56%,
    rgba(140, 198, 246, 0.03) 66%,
    transparent 78%
  );
  pointer-events: none;
  z-index: 0;
  will-change: transform;
}

.hero-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr;
  gap: 56px;
  align-items: center;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 22px;
  max-width: 680px; /* Platz für das breiteste Flip-Wort „MISSING CONTEXT" */
}

/* Logo über dem Titel: groß und weiß — die Marke selbst trägt, keine Effekte */
.hero-mark-slot {
  margin-bottom: 6px;
}

.hero-mark {
  display: block;
  width: 88px;
  height: 88px;
}

/* ── Titel + 3D-Split-Flap ───────────────────────────────────────────────── */
.hero-title {
  /* Größe so, dass das breiteste Flip-Wort („MISSING CONTEXT") in die
     Text-Spalte passt und nicht ans Forum-Mock stößt */
  font-size: clamp(2.4rem, 4.6vw, 4rem);
  font-weight: 800;
  line-height: 1.04;
  letter-spacing: -0.03em;
  color: var(--nb-ink);
  white-space: nowrap;
}

.hero-flip-frame {
  display: inline-flex;
  align-items: baseline;
  white-space: nowrap;
}

.hero-flip-bracket {
  display: inline-block; /* transformierbar */
  position: relative;
  /* Optisch zentrieren: die Klammer reicht unter die Grundlinie, die
     Versalien nicht — gemessen sitzt ihre Mitte 0.075em tiefer */
  top: -0.075em;
  color: var(--nb-ink-faint);
  font-weight: 400;
}

.hero-flip-bracket-r {
  will-change: transform;
}

.hero-flip-word {
  display: inline-block; /* messbar für die Klammer-Position */
}

.hero-flip {
  position: relative;
  display: inline-grid;
  margin: 0 0.18em;
  perspective: 900px;
  transform-style: preserve-3d;
}

/* Alle Varianten als unsichtbare Sizer übereinander — die Box ist immer so
   breit wie die tatsächlich breiteste Variante. Kein Reflow, nie. */
.hero-flip-sizer {
  grid-area: 1 / 1;
  visibility: hidden;
  white-space: nowrap;
  font-weight: 800;
}

.hero-flip-sizer::before {
  content: attr(data-word);
}

.hero-flip-face {
  position: absolute;
  inset: 0;
  display: inline-block;
  white-space: nowrap;
  color: var(--nb-accent-soft);
  font-weight: 800;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  will-change: transform, opacity;
  visibility: hidden; /* GSAP übernimmt via autoAlpha */
}

/* Das erste Wort steht schon im SSR-Paint — keine leere Klammer, bevor
   GSAP übernimmt */
.hero-flip-face.is-initial {
  visibility: visible;
}

.hero-sub {
  font-size: 1.18rem;
  line-height: 1.6;
  max-width: 54ch;
}

.hero-ctas {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.hero-cta-primary {
  background: var(--nb-accent);
  color: #fff;
  border-radius: var(--radius-pill);
  padding-inline: 22px;
  transition:
    background var(--dur) var(--ease-out),
    transform var(--dur) var(--ease-out);
}

.hero-cta-primary:hover {
  background: var(--nb-accent-hover);
  transform: translateY(-2px);
}

.hero-cta-primary:active {
  transform: translateY(0) scale(0.98);
}

.hero-cta-secondary {
  border-radius: var(--radius-pill);
  padding-inline: 18px;
}

/* Fakten-Band: Full-Bleed-Streifen zwischen zwei Section-Rules. Innen drei
   Zellen in Gerüst-Breite — Hairline-Trenner, Plus-Marks an den Kreuzungen,
   ein Akzent-Dash auf der Oberkante jeder Zelle (wie vor den Section-Titeln).
   Zahl im Display-Schnitt des Titels, Label in Mono wie die Kapitel-Leiste. */
.hero-proof {
  position: relative;
  z-index: 1;
  margin-top: clamp(64px, 9vh, 112px);
  background: var(--nb-surface-soft);
  /* Unterkante = Section-Divider der Story direkt darunter (mit Plus-Marks) */
}

.hero-proof-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.hero-stat {
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 30px var(--nb-gutter) 32px;
}

.hero-stat + .hero-stat {
  border-left: 1px solid var(--nb-rail);
}

.hero-stat::after {
  content: '';
  position: absolute;
  top: -1px;
  left: var(--nb-gutter);
  width: 26px;
  height: 2px;
  background: var(--nb-accent-soft);
}

.hero-stat-value {
  flex-shrink: 0;
  min-width: 0.7em;
  font-size: clamp(2.4rem, 3.4vw, 3.1rem);
  font-weight: 800;
  line-height: 0.9;
  letter-spacing: -0.03em;
  color: var(--nb-ink);
  font-variant-numeric: tabular-nums;
}

.hero-stat-copy {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.hero-stat-title {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nb-ink);
}

.hero-stat-text {
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--nb-ink-faint);
}

/* ── Konzept: Chaos-Tunnel um die geordnete Mitte ────────────────────────── */
.hero-visual {
  position: relative;
  min-height: 520px;
}

/* Schwebende Chaos-Kacheln (Discord-Channel-/DM-Optik) */
.hero-chaos {
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: var(--font-discord);
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--dc-ink-muted);
  background: var(--dc-bg-sidebar);
  border: 1px solid var(--dc-border-card);
  border-radius: 10px;
  padding: 8px 12px;
  white-space: nowrap;
  will-change: transform;
  pointer-events: none; /* Deko blockiert nie die interaktive Karte */
}

/* Die Kachel-Texte sind reine Dekoration (unscharfe Chaos-Kulisse, aria-hidden):
   sie kommen als CSS-Content aus data-text — so stehen sie weder im
   Accessibility-Tree noch fallen sie unter die Kontrast-Anforderung für Text. */
.hero-chaos-hash::before,
.hero-chaos-label::before,
.hero-chaos-badge::before {
  content: attr(data-text);
}

/* Tunnel-Tiefe: fern = klein + blass, nah = groß + sehr blass. Der
   Größensprung zwischen den Ebenen macht die Fokallänge; die Unschärfe von
   früher übernimmt die Deckkraft (blur-Filter auf dauerhaft treibenden
   Elementen kostet pro Frame). */
.hero-chaos-far {
  transform: scale(0.68);
  opacity: 0.22;
  z-index: 1;
}

.hero-chaos-mid {
  transform: scale(0.95);
  opacity: 0.42;
  z-index: 1;
}

.hero-chaos-near {
  transform: scale(1.42);
  opacity: 0.16;
  z-index: 3;
}

.hero-chaos-hash {
  font-size: 1rem;
  color: var(--dc-ink-channel);
  line-height: 1;
}

.hero-chaos-badge {
  background: var(--dc-red);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  border-radius: var(--radius-pill);
  padding: 1px 7px;
}

/* Ticket-Chips (alte Tickets treiben im Hintergrund) */
/* Positionen: fern kreist eng um die Mitte, nah sitzt am Rand (Tunnel) */
.hero-chaos-far0 { top: 6%; left: 26%; }
.hero-chaos-far1 { top: 14%; right: 22%; }
.hero-chaos-far2 { bottom: 12%; left: 30%; }
.hero-chaos-far3 { bottom: 6%; right: 26%; }
.hero-chaos-far4 { top: 30%; left: 16%; }
.hero-chaos-far5 { bottom: 30%; right: 14%; }
.hero-chaos-mid0 { top: 2%; left: 2%; }
.hero-chaos-mid1 { top: 32%; right: -4%; }
.hero-chaos-mid2 { bottom: 26%; left: -6%; }
.hero-chaos-mid3 { bottom: -2%; right: 8%; }
.hero-chaos-mid4 { top: -4%; right: 30%; }
.hero-chaos-mid5 { bottom: -8%; left: 24%; }
.hero-chaos-near0 { top: 62%; left: 60%; }
.hero-chaos-near1 { top: 8%; right: -8%; }
.hero-chaos-near2 { top: 48%; left: -10%; }

/* Die geordnete Mitte — breit genug, dass Titel nicht abschneiden,
   und nur noch ein Hauch Akzent-Glow */
.hero-order {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  width: min(420px, 100%);
  background: var(--dc-bg-chat);
  border: 1px solid var(--dc-border-card);
  border-radius: var(--radius-md);
  padding: 12px 12px 10px;
  box-shadow:
    var(--shadow-float),
    0 0 22px -16px var(--nb-accent-glow);
}

.hero-order-head {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-bottom: 10px;
}

.hero-order-logo {
  border-radius: 6px;
  display: block;
}

.hero-order-name {
  font-family: var(--font-discord);
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--dc-ink-header);
}

/* Forum-Signatur: große Suchleiste + New Post */
.hero-order-toolbar {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-bottom: 10px;
}

.hero-order-search {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  color: var(--dc-ink-muted); /* echtes Input: AA-Kontrast auf --dc-bg-input */
  font-family: var(--font-discord);
  font-size: 0.7rem;
  border-radius: var(--dc-radius-msg);
  padding: 5px 9px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  cursor: text;
  transition: border-color var(--dur-fast) var(--ease-out);
}

/* Fokus-Ring auf dem Wrapper (der Input selbst hat keinen) — ≥3:1 */
.hero-order-search:focus-within {
  outline: 2px solid var(--nb-accent-soft);
  outline-offset: 2px;
  border-color: var(--nb-accent-border);
}

.hero-order-search-input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 0;
  outline: none;
  font-family: inherit;
  font-size: inherit;
  color: var(--dc-ink);
  caret-color: var(--nb-accent-soft);
}

.hero-order-search-input::placeholder {
  color: var(--dc-ink-muted);
}

.hero-order-newpost {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--dc-blurple);
  color: #fff;
  font-family: var(--font-discord);
  font-size: 0.7rem;
  font-weight: 500;
  border-radius: var(--dc-radius-msg);
  padding: 5px 9px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Kategorie-Filterzeile — macht das Sortier-Versprechen sichtbar */
.hero-order-filters {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  padding-bottom: 10px;
  overflow: hidden;
}

.hero-order-sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-discord);
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--dc-ink);
  white-space: nowrap;
  flex-shrink: 0;
}

.hero-order-filters-divider {
  width: 1px;
  height: 16px;
  background: var(--dc-border-card);
  margin: 0 2px;
  flex-shrink: 0;
}

.hero-order-all {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-discord);
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--dc-ink);
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: var(--radius-pill);
  padding: 2px 9px;
  flex-shrink: 0;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out);
}

.hero-order-all:hover {
  border-color: var(--dc-ink-channel);
}

.hero-order-tagbtn {
  display: inline-flex;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.hero-order-tagbtn:hover :deep(.dc-tag) {
  border-color: var(--dc-ink-channel);
}

.hero-order-filters :deep(.dc-tag) {
  font-size: 0.68rem;
  padding: 2px 9px;
  flex-shrink: 0;
}

/* FIXE Listenhöhe (exakt 3 Zeilen à 107px + 2×7px Abstand, live vermessen)
   — die Karte ändert ihre Höhe nie, egal wie gefiltert wird */
.hero-order-list {
  position: relative;
  height: 335px;
  overflow: hidden;
}

.hero-order-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 100%;
  font-family: var(--font-discord);
  font-size: 0.74rem;
  color: var(--dc-ink-muted);
}

/* Fester Slot für „+n more" — immer gleich hoch, auch wenn leer */
.hero-order-footerline {
  height: 16px;
  margin-top: 4px;
  font-family: var(--font-discord);
  font-size: 0.66rem;
  color: var(--dc-ink-muted);
  text-align: center;
  line-height: 16px;
}

/* Kategorie-Wechsel: ein Crossfade der ganzen Liste, out-in = kein Overlap */
.swap-enter-active {
  transition:
    opacity 200ms var(--ease-out),
    transform 200ms var(--ease-out);
}

.swap-leave-active {
  transition:
    opacity 130ms var(--ease-out),
    transform 130ms var(--ease-out);
}

.swap-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.swap-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Post-Zeilen: Tag → Titel → Autor → Meta, Thumbnail rechts */
.hero-order-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: var(--dc-bg-card);
  border: 1px solid var(--dc-border-card);
  border-radius: var(--dc-radius-btn);
  padding: 8px 10px;
  margin-top: 7px;
}

.hero-order-row:first-of-type {
  margin-top: 0;
}

.hero-order-main {
  flex: 1;
  min-width: 0;
}

.hero-order-title {
  font-family: var(--font-discord);
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--dc-ink-header);
  margin-top: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero-order-author {
  font-family: var(--font-discord);
  font-size: 0.64rem;
  font-weight: 600;
  color: var(--dc-ink);
  margin-top: 2px;
}

.hero-order-meta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-discord);
  font-size: 0.64rem;
  color: var(--dc-ink-muted);
  margin-top: 5px;
}

.hero-order-envelope {
  display: grid;
  place-items: center;
  width: 18px;
  height: 15px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: 4px;
  color: var(--dc-ink-muted);
  margin-right: 2px;
}

.hero-order-dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--dc-ink-muted);
}

.hero-order-thumb {
  flex-shrink: 0;
  border-radius: 10px;
  display: block;
}

.hero-order-row :deep(.dc-tag) {
  font-size: 0.68rem;
  padding: 2px 9px;
  flex-shrink: 0;
}

@media (max-width: 767px) {
  /* Ohne section-rules (mobil ausgeblendet) braucht das Band eigene Kanten */
  .hero-proof {
    border-top: 1px solid var(--nb-rail);
    border-bottom: 1px solid var(--nb-rail);
  }

  /* Gestapelt: Trenner horizontal, ohne Plus-Marks (Rails sind mobil aus) */
  .hero-proof-grid {
    grid-template-columns: 1fr;
  }

  .hero-stat {
    padding-block: 20px;
  }

  .hero-stat + .hero-stat {
    border-left: 0;
    border-top: 1px solid var(--nb-rail);
  }

  .hero-stat-plus {
    display: none;
  }

  .hero-mark {
    width: 64px;
    height: 64px;
  }
}

@media (max-width: 640px) {
  .hero-title {
    white-space: normal;
  }

  .hero-flip-frame {
    font-size: 0.82em;
  }

  .hero-visual {
    min-height: 380px;
  }
}

@media (min-width: 1000px) {
  .hero-grid {
    grid-template-columns: 1.1fr 0.9fr;
  }

  /* Das Forum-Mock etwas größer: zoom skaliert Layout + Schrift gleichmäßig
     (scharf, kein Transform — der gehört GSAP für Entrance und Atmen) */
  .hero-visual {
    min-height: 600px;
  }

  .hero-order {
    zoom: 1.14;
  }
}
</style>
